import { getDatabase, ref, get, set, push, update, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import {
    ProjectModel,
    ProjectStatus,
    ProjectNote,
    ProjectChecklist,
    ChecklistItem,
    BudgetAllocation
} from '../types/projects';
import { getAssignees, getDelegations } from '../types/assignees';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { BuildingType } from '../types/BuildingTypeEnums';
import { GroupTag } from '../types/protocols';
import { uploadProjectPhoto, ProjectPhotoType, ProjectPhotoUploadResult } from '../Helpers/ProjectPhotoUtils';
import {
    uploadProjectAttachment,
    deleteProjectAttachment,
    ProjectAttachmentUploadResult
} from '../Helpers/ProjectAttachmentUtils';
import { softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';

export interface ProjectWithBuilding extends ProjectModel {
    buildingKey: string;
    buildingName?: string;
    buildingType?: BuildingType;
    buildingCity?: string;
    buildingMunicipality?: string;
    buildingCoverPhotoPath?: string;
    buildingGroupTags?: { [key: string]: GroupTag };
}

export class ProjectsApi {
    private db = getDatabase();

    private getProjectPath(projectKey: string): string {
        return `projects/${projectKey}`;
    }

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Validate and parse project data from Firebase
     * Returns null if required fields are missing
     */
    private parseProjectData(
        data: unknown,
        projectKey: string,
        buildingKey: string,
        buildingName?: string,
        buildingType?: BuildingType,
        buildingCity?: string,
        buildingMunicipality?: string,
        buildingCoverPhotoPath?: string,
        buildingGroupTags?: { [key: string]: GroupTag },
    ): ProjectWithBuilding | null {
        if (!data || typeof data !== 'object') {
            return null;
        }

        const d = data as Record<string, unknown>;

        // Required fields
        if (typeof d.title !== 'string' || typeof d.status !== 'string') {
            console.warn(`Invalid project data at ${buildingKey}/${projectKey}: missing title or status`);
            return null;
        }

        // Validate status is a valid enum value
        if (!Object.values(ProjectStatus).includes(d.status as ProjectStatus)) {
            console.warn(`Invalid project status at ${buildingKey}/${projectKey}: ${d.status}`);
            return null;
        }

        return {
            key: projectKey,
            buildingKey,
            buildingName,
            buildingType,
            buildingCity,
            buildingMunicipality,
            buildingCoverPhotoPath,
            buildingGroupTags,
            ...d
        } as ProjectWithBuilding;
    }

    // ============ READ OPERATIONS ============

    /**
     * Get a single project by key (one-time read)
     * Note: buildingKey parameter kept for API compatibility but not used in path
     */
    async getProject(buildingKey: string, projectKey: string): Promise<ProjectWithBuilding | null> {
        const [projectSnapshot, summarySnapshot] = await Promise.all([
            get(ref(this.db, this.getProjectPath(projectKey))),
            get(ref(this.db, `buildingsSummary/${buildingKey}`))
        ]);

        if (projectSnapshot.exists()) {
            const s = summarySnapshot.exists() ? summarySnapshot.val() : undefined;
            return this.parseProjectData(
                projectSnapshot.val(), projectKey, buildingKey,
                s?.name, s?.buildingType, s?.buildingInfo?.city,
                s?.buildingInfo?.municipality, undefined, s?.groupTags
            );
        }
        return null;
    }

    /**
     * Get all projects with building names (flat structure query)
     */
    async getAllProjects(): Promise<ProjectWithBuilding[]> {
        // Fetch projects and building summaries in parallel
        const [projectsSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, 'projects')),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        if (!projectsSnapshot.exists()) {
            return [];
        }

        interface BuildingMeta {
            name?: string;
            type?: BuildingType;
            city?: string;
            municipality?: string;
            coverPhotoPath?: string;
            groupTags?: { [key: string]: GroupTag };
        }
        const buildingMeta: Record<string, BuildingMeta> = {};
        if (buildingsSnapshot.exists()) {
            buildingsSnapshot.forEach((child) => {
                const s = child.val();
                if (!s || !child.key) return;
                const coverPhoto = s.coverPhoto?.storagePath;
                const coverPhotos = s.coverPhotos;
                const currentKey = s.currentCoverPhotoKey;
                const coverPhotoPath = (currentKey && coverPhotos?.[currentKey]?.storagePath)
                    || coverPhoto;
                buildingMeta[child.key] = {
                    name: s.name,
                    type: s.buildingType as BuildingType | undefined,
                    city: s.buildingInfo?.city,
                    municipality: s.buildingInfo?.municipality,
                    coverPhotoPath,
                    groupTags: s.groupTags,
                };
            });
        }

        // Parse all projects from flat structure
        const projects: ProjectWithBuilding[] = [];
        projectsSnapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            const buildingKey = data?.buildingKey || '';
            const meta = buildingMeta[buildingKey];

            const project = this.parseProjectData(
                data,
                key,
                buildingKey,
                meta?.name,
                meta?.type,
                meta?.city,
                meta?.municipality,
                meta?.coverPhotoPath,
                meta?.groupTags,
            );
            if (project) {
                projects.push(project);
            }
        });

        return projects;
    }

    /**
     * Get projects for a specific building (uses indexed query on buildingKey)
     */
    async getProjectsByBuilding(buildingKey: string): Promise<ProjectWithBuilding[]> {
        const { query, orderByChild, equalTo } = await import('firebase/database');

        // Query projects by buildingKey index and fetch building summary in parallel
        const projectsQuery = query(
            ref(this.db, 'projects'),
            orderByChild('buildingKey'),
            equalTo(buildingKey)
        );

        const [projectsSnapshot, summarySnapshot] = await Promise.all([
            get(projectsQuery),
            get(ref(this.db, `buildingsSummary/${buildingKey}`))
        ]);

        if (!projectsSnapshot.exists()) {
            return [];
        }

        const s = summarySnapshot.exists() ? summarySnapshot.val() : undefined;
        const buildingName = s?.name;
        const buildingType = s?.buildingType as BuildingType | undefined;
        const buildingCity = s?.buildingInfo?.city;
        const buildingMunicipality = s?.buildingInfo?.municipality;
        const buildingGroupTags = s?.groupTags as { [key: string]: GroupTag } | undefined;
        const coverPhotoPath = (s?.currentCoverPhotoKey && s?.coverPhotos?.[s.currentCoverPhotoKey]?.storagePath)
            || s?.coverPhoto?.storagePath;

        const projects: ProjectWithBuilding[] = [];
        projectsSnapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const project = this.parseProjectData(
                child.val(), key, buildingKey,
                buildingName, buildingType,
                buildingCity, buildingMunicipality, coverPhotoPath,
                buildingGroupTags
            );
            if (project) {
                projects.push(project);
            }
        });

        return projects;
    }

    // ============ CREATE ============

    /**
     * Create a new project (flat structure: projects/{projectKey})
     */
    async createProject(
        buildingKey: string,
        title: string,
        startDate: string,
        plannedEndDate: string
    ): Promise<{ success: boolean; projectKey?: string; error?: string }> {
        if (!title.trim()) {
            return { success: false, error: 'Titel krävs' };
        }
        if (!startDate) {
            return { success: false, error: 'Startdatum krävs' };
        }
        if (!plannedEndDate) {
            return { success: false, error: 'Planerat slutdatum krävs' };
        }

        try {
            const user = this.getCurrentUser();
            // Use flat structure: projects/{projectKey}
            const projectsRef = ref(this.db, 'projects');
            const newProjectRef = push(projectsRef);
            const projectKey = newProjectRef.key!;

            const project = {
                key: projectKey,
                buildingKey, // Stored as field for indexed queries
                title: title.trim(),
                status: ProjectStatus.PLANNED,
                startDate,
                plannedEndDate,
                createdBy: user.uid,
                createdAt: formatDateForStorage(new Date())
            };

            await set(newProjectRef, project);
            recordActivity('project.created', { buildingKey, targetKey: projectKey, label: project.title });

            return { success: true, projectKey };
        } catch (error) {
            console.error('Error creating project:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Kunde inte skapa projektet'
            };
        }
    }

    // ============ UPDATE OPERATIONS ============

    /**
     * Update project title
     */
    async updateTitle(buildingKey: string, projectKey: string, title: string): Promise<void> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }

        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        await update(projectRef, {
            title: title.trim(),
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey, label: title.trim() });
    }

    /**
     * Update project description (free text).
     * Stores null when cleared so the field is removed from the node.
     */
    async updateDescription(buildingKey: string, projectKey: string, description: string): Promise<void> {
        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        const trimmed = description.trim();
        await update(projectRef, {
            description: trimmed || null,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update "Ansvarig beställare" (responsible orderer) — an informational
     * reference to an internal user. Pass null to clear.
     */
    async updateResponsibleOrderer(buildingKey: string, projectKey: string, userId: string | null): Promise<void> {
        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        await update(projectRef, {
            responsibleOrderer: userId || null,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update "Sekundär beställare" (secondary orderer) — an informational
     * reference to an internal user. Pass null to clear.
     */
    async updateSecondaryOrderer(buildingKey: string, projectKey: string, userId: string | null): Promise<void> {
        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        await update(projectRef, {
            secondaryOrderer: userId || null,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update project status
     */
    async updateStatus(
        buildingKey: string,
        projectKey: string,
        status: ProjectStatus,
        actualEndDate?: string
    ): Promise<void> {
        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        const updates: Partial<ProjectModel> = {
            status,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        };

        if (status === ProjectStatus.COMPLETED && actualEndDate) {
            updates.actualEndDate = actualEndDate;
        }

        await update(projectRef, updates);
        recordActivity(status === ProjectStatus.COMPLETED ? 'project.completed' : 'project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Reopen a completed project (clear actualEndDate) - atomic operation
     */
    async reopenProject(buildingKey: string, projectKey: string): Promise<void> {
        const user = this.getCurrentUser();
        const path = this.getProjectPath(projectKey);

        // Use multi-path update for atomicity
        const updates: Record<string, string | null> = {};
        updates[`${path}/actualEndDate`] = null;
        updates[`${path}/updatedBy`] = user.uid;
        updates[`${path}/updatedAt`] = formatDateForStorage(new Date());

        await update(ref(this.db), updates);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update project start date
     */
    async updateStartDate(buildingKey: string, projectKey: string, startDate: string): Promise<void> {
        if (!startDate) {
            throw new Error('Startdatum krävs');
        }

        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        await update(projectRef, {
            startDate,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update project planned end date
     */
    async updatePlannedEndDate(buildingKey: string, projectKey: string, plannedEndDate: string): Promise<void> {
        if (!plannedEndDate) {
            throw new Error('Planerat slutdatum krävs');
        }

        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        await update(projectRef, {
            plannedEndDate,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update project actual end date
     */
    async updateActualEndDate(buildingKey: string, projectKey: string, actualEndDate: string): Promise<void> {
        if (!actualEndDate) {
            throw new Error('Slutdatum krävs');
        }

        const user = this.getCurrentUser();
        const projectRef = ref(this.db, this.getProjectPath(projectKey));

        await update(projectRef, {
            actualEndDate,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Delete a project
     * Throws an error if work orders are still linked to the project. Every
     * delegate's `externalDelegations` mirror entry is removed in the same
     * update, so the project leaves their portal.
     */
    async deleteProject(buildingKey: string, projectKey: string): Promise<void> {
        // Check for linked work orders before deletion
        const workOrdersRef = ref(this.db, 'workOrders/active');
        const snapshot = await get(workOrdersRef);

        if (snapshot.exists()) {
            const workOrders = snapshot.val();
            const linkedWorkOrders = Object.entries(workOrders)
                .filter(([, wo]) => (wo as { projectKey?: string }).projectKey === projectKey);

            if (linkedWorkOrders.length > 0) {
                throw new Error(
                    `Kan inte ta bort projektet. ${linkedWorkOrders.length} ärenden är kopplade till projektet. Ta bort kopplingarna först.`
                );
            }
        }

        const projectPath = this.getProjectPath(projectKey);
        const projectSnapshot = await get(ref(this.db, projectPath));
        const updates: Record<string, null> = { [projectPath]: null };
        for (const externalUserId of Object.keys(getDelegations(projectSnapshot.val()))) {
            updates[`externalDelegations/${externalUserId}/${projectKey}`] = null;
        }
        await update(ref(this.db), updates);
        recordActivity('project.deleted', { buildingKey, targetKey: projectKey, label: projectSnapshot.val()?.title });
    }

    /**
     * Update the project's budget allocation (the three category amounts).
     * Stored as the single `budget` object node; the total is never persisted.
     * Pass null to clear the budget.
     */
    async updateBudgetAllocation(buildingKey: string, projectKey: string, allocation: BudgetAllocation | null): Promise<void> {
        if (allocation !== null) {
            const amounts = [
                allocation.investmentAmount,
                allocation.buildingMaintenanceAmount,
                allocation.propertyTechMaintenanceAmount
            ];
            if (amounts.some(amount => amount < 0 || !Number.isFinite(amount))) {
                throw new Error('Budgetfördelning måste vara positiva tal');
            }
        }

        const user = this.getCurrentUser();
        const path = this.getProjectPath(projectKey);

        await update(ref(this.db, path), {
            budget: allocation, // object when set, null clears the node
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    // ============ NOTES ============

    /**
     * Add a note to a project
     */
    async addNote(
        buildingKey: string,
        projectKey: string,
        title: string,
        content: string
    ): Promise<ProjectNote> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }
        if (!content.trim()) {
            throw new Error('Innehåll krävs');
        }

        const user = this.getCurrentUser();
        const notesRef = ref(this.db, `${this.getProjectPath(projectKey)}/notes`);
        const newNoteRef = push(notesRef);
        const noteKey = newNoteRef.key!;

        const note: ProjectNote = {
            key: noteKey,
            title: title.trim(),
            content: content.trim(),
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date())
        };

        await set(newNoteRef, note);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
        return note;
    }

    /**
     * Update a note
     */
    async updateNote(
        buildingKey: string,
        projectKey: string,
        noteKey: string,
        title: string,
        content: string
    ): Promise<void> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }
        if (!content.trim()) {
            throw new Error('Innehåll krävs');
        }

        const user = this.getCurrentUser();
        const noteRef = ref(this.db, `${this.getProjectPath(projectKey)}/notes/${noteKey}`);

        await update(noteRef, {
            title: title.trim(),
            content: content.trim(),
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Delete a note
     */
    async deleteNote(buildingKey: string, projectKey: string, noteKey: string): Promise<void> {
        const noteRef = ref(this.db, `${this.getProjectPath(projectKey)}/notes/${noteKey}`);
        await remove(noteRef);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    // ============ CHECKLISTS ============

    /**
     * Add a checklist to a project
     */
    async addChecklist(buildingKey: string, projectKey: string, title: string): Promise<ProjectChecklist> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }

        const user = this.getCurrentUser();
        const checklistsRef = ref(this.db, `${this.getProjectPath(projectKey)}/checklists`);
        const newChecklistRef = push(checklistsRef);
        const checklistKey = newChecklistRef.key!;

        const checklist: ProjectChecklist = {
            key: checklistKey,
            title: title.trim(),
            items: {},
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date())
        };

        await set(newChecklistRef, checklist);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
        return checklist;
    }

    /**
     * Update a checklist title
     */
    async updateChecklistTitle(
        buildingKey: string,
        projectKey: string,
        checklistKey: string,
        title: string
    ): Promise<void> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }

        const checklistRef = ref(this.db, `${this.getProjectPath(projectKey)}/checklists/${checklistKey}`);
        await update(checklistRef, { title: title.trim() });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Delete a checklist
     */
    async deleteChecklist(buildingKey: string, projectKey: string, checklistKey: string): Promise<void> {
        const checklistRef = ref(this.db, `${this.getProjectPath(projectKey)}/checklists/${checklistKey}`);
        await remove(checklistRef);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Add a checklist item
     */
    async addChecklistItem(
        buildingKey: string,
        projectKey: string,
        checklistKey: string,
        text: string
    ): Promise<ChecklistItem> {
        if (!text.trim()) {
            throw new Error('Text krävs');
        }

        const itemsRef = ref(this.db, `${this.getProjectPath(projectKey)}/checklists/${checklistKey}/items`);

        // Get existing items to determine next sortOrder
        const existingSnapshot = await get(itemsRef);
        let maxSortOrder = -1;
        if (existingSnapshot.exists()) {
            const items = existingSnapshot.val() as Record<string, ChecklistItem>;
            Object.values(items).forEach(item => {
                const order = item.sortOrder ?? 0;
                if (order > maxSortOrder) {
                    maxSortOrder = order;
                }
            });
        }

        const newItemRef = push(itemsRef);
        const itemKey = newItemRef.key!;

        const item: ChecklistItem = {
            key: itemKey,
            text: text.trim(),
            completed: false,
            sortOrder: maxSortOrder + 1
        };

        await set(newItemRef, item);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
        return item;
    }

    /**
     * Update a checklist item text
     */
    async updateChecklistItemText(
        buildingKey: string,
        projectKey: string,
        checklistKey: string,
        itemKey: string,
        text: string
    ): Promise<void> {
        if (!text.trim()) {
            throw new Error('Text krävs');
        }

        const itemRef = ref(this.db, `${this.getProjectPath(projectKey)}/checklists/${checklistKey}/items/${itemKey}`);
        await update(itemRef, { text: text.trim() });
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Toggle a checklist item completion - atomic operation
     */
    async toggleChecklistItem(
        buildingKey: string,
        projectKey: string,
        checklistKey: string,
        itemKey: string,
        completed: boolean
    ): Promise<void> {
        const user = this.getCurrentUser();
        const basePath = `${this.getProjectPath(projectKey)}/checklists/${checklistKey}/items/${itemKey}`;

        if (completed) {
            await update(ref(this.db, basePath), {
                completed: true,
                completedBy: user.uid,
                completedAt: formatDateForStorage(new Date())
            });
        } else {
            // Use multi-path update for atomicity
            const updates: Record<string, boolean | null> = {};
            updates[`${basePath}/completed`] = false;
            updates[`${basePath}/completedBy`] = null;
            updates[`${basePath}/completedAt`] = null;

            await update(ref(this.db), updates);
        }
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Delete a checklist item
     */
    async deleteChecklistItem(
        buildingKey: string,
        projectKey: string,
        checklistKey: string,
        itemKey: string
    ): Promise<void> {
        const itemRef = ref(this.db, `${this.getProjectPath(projectKey)}/checklists/${checklistKey}/items/${itemKey}`);
        await remove(itemRef);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Reorder checklist items - updates sortOrder for all items
     */
    async reorderChecklistItems(
        buildingKey: string,
        projectKey: string,
        checklistKey: string,
        orderedItemKeys: string[]
    ): Promise<void> {
        const basePath = `${this.getProjectPath(projectKey)}/checklists/${checklistKey}/items`;

        // Use multi-path update for atomicity
        const updates: Record<string, number> = {};
        orderedItemKeys.forEach((itemKey, index) => {
            updates[`${basePath}/${itemKey}/sortOrder`] = index;
        });

        await update(ref(this.db), updates);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    // ============ PHOTOS ============

    /**
     * Upload a photo to a project
     */
    async uploadPhoto(
        buildingKey: string,
        projectKey: string,
        photoType: ProjectPhotoType,
        file: File
    ): Promise<ProjectPhotoUploadResult> {
        const user = this.getCurrentUser();
        return uploadProjectPhoto(buildingKey, projectKey, photoType, file, user.uid);
    }

    /**
     * Delete a photo from a project (soft delete — tombstones the metadata)
     */
    async deletePhoto(
        buildingKey: string,
        projectKey: string,
        photoType: ProjectPhotoType,
        photoKey: string
    ): Promise<void> {
        await softDeleteFile({
            originalPath: `${this.getProjectPath(projectKey)}/${photoType}/${photoKey}`,
            entityType: 'projectPhoto',
        });
    }

    // ============ ATTACHMENTS ============

    /**
     * Upload an attachment to a project
     */
    async uploadAttachment(
        buildingKey: string,
        projectKey: string,
        file: File,
        displayName?: string
    ): Promise<ProjectAttachmentUploadResult> {
        const user = this.getCurrentUser();
        return uploadProjectAttachment(buildingKey, projectKey, file, user.uid, displayName);
    }

    /**
     * Delete an attachment from a project
     */
    async deleteAttachment(
        buildingKey: string,
        projectKey: string,
        attachmentKey: string
    ): Promise<{ success: boolean; error?: string }> {
        return deleteProjectAttachment(buildingKey, projectKey, attachmentKey);
    }

    /**
     * Change an attachment's display name. The file and its original
     * `fileName` are untouched; an empty name removes the custom name so
     * readers fall back to `fileName`.
     */
    async renameAttachment(projectKey: string, attachmentKey: string, displayName: string | undefined): Promise<void> {
        this.getCurrentUser();
        const snapshot = await get(ref(this.db, `projects/${projectKey}/attachments/${attachmentKey}`));
        if (!snapshot.exists()) throw new Error('Bilagan hittades inte');
        await update(ref(this.db, `projects/${projectKey}/attachments/${attachmentKey}`), {
            displayName: displayName ?? null,
        });
        recordActivity('document.updated', { targetKey: projectKey });
    }

    // ============ ASSIGNMENT ============

    /**
     * Compatibility shim over the multi-assignee model (mirrors
     * workOrdersApi.assign): single-select "assign" replaces the internal
     * assignees with just this user, or clears them when userId is null.
     * External delegates are NOT touched (fully mixed).
     */
    async assign(projectKey: string, userId: string | null, sendEmail: boolean = true): Promise<void> {
        await this.setAssignees(projectKey, userId === null ? [] : [userId], userId !== null && sendEmail);
    }

    /**
     * Add a single internal assignee without disturbing existing assignees or
     * delegates. Migrates a legacy scalar assignedTo into the map.
     */
    async addAssignee(projectKey: string, userId: string, sendEmail: boolean = true): Promise<void> {
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());
        const path = this.getProjectPath(projectKey);

        const snapshot = await get(ref(this.db, path));
        if (!snapshot.exists()) {
            throw new Error('Projektet hittades inte');
        }
        const project = snapshot.val() as ProjectModel;

        const updates: Record<string, unknown> = {
            [`${path}/updatedBy`]: user.uid,
            [`${path}/updatedAt`]: now,
        };

        if (project.assignedTo) {
            const legacy = project.assignedTo;
            if (legacy !== userId) {
                updates[`${path}/assignees/${legacy}`] = { assignedBy: '', assignedAt: '' };
            }
            updates[`${path}/assignedTo`] = null;
        }

        updates[`${path}/assignees/${userId}`] = { assignedBy: user.uid, assignedAt: now };
        updates[`${path}/sendAssignmentEmail`] = sendEmail ? true : null;

        await update(ref(this.db), updates);
        recordActivity('project.updated', { buildingKey: project.buildingKey, targetKey: projectKey, label: project.title });
    }

    /**
     * Remove a single internal assignee. Delegates and other assignees untouched.
     */
    async removeAssignee(projectKey: string, userId: string): Promise<void> {
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());
        const path = this.getProjectPath(projectKey);

        const snapshot = await get(ref(this.db, path));
        if (!snapshot.exists()) {
            throw new Error('Projektet hittades inte');
        }
        const project = snapshot.val() as ProjectModel;

        const updates: Record<string, unknown> = {
            [`${path}/assignees/${userId}`]: null,
            [`${path}/updatedBy`]: user.uid,
            [`${path}/updatedAt`]: now,
        };
        if (project.assignedTo === userId) {
            updates[`${path}/assignedTo`] = null;
        }

        await update(ref(this.db), updates);
        recordActivity('project.updated', { buildingKey: project.buildingKey, targetKey: projectKey, label: project.title });
    }

    /**
     * Replace the full set of internal assignees. Delegates untouched.
     */
    async setAssignees(projectKey: string, userIds: string[], sendEmail: boolean = true): Promise<void> {
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());
        const path = this.getProjectPath(projectKey);

        const snapshot = await get(ref(this.db, path));
        if (!snapshot.exists()) {
            throw new Error('Projektet hittades inte');
        }
        const project = snapshot.val() as ProjectModel;

        const existing = getAssignees(project);
        const nextAssignees: Record<string, { assignedBy: string; assignedAt: string }> = {};
        for (const uid of userIds) {
            nextAssignees[uid] = existing[uid] || { assignedBy: user.uid, assignedAt: now };
        }
        const anyAdded = userIds.some((uid) => !existing[uid]);

        const updates: Record<string, unknown> = {
            [`${path}/assignees`]: userIds.length > 0 ? nextAssignees : null,
            [`${path}/assignedTo`]: null,
            [`${path}/updatedBy`]: user.uid,
            [`${path}/updatedAt`]: now,
            [`${path}/sendAssignmentEmail`]: anyAdded && sendEmail ? true : null,
        };

        await update(ref(this.db), updates);
        recordActivity('project.updated', { buildingKey: project.buildingKey, targetKey: projectKey, label: project.title });
    }

    // ============ EXTERNAL CONTACTS ============

    /**
     * Add a contact (internal team member or external party) to a project.
     * Stored under the externalContacts node regardless of type; contactType
     * records which kind it is (absent legacy records are treated as external).
     */
    async addExternalContact(
        buildingKey: string,
        projectKey: string,
        externalUserId: string,
        projectNote?: string,
        contactType: 'internal' | 'external' = 'external'
    ): Promise<void> {
        if (!externalUserId) {
            throw new Error('Användare krävs');
        }

        const user = this.getCurrentUser();
        const contactPath = `${this.getProjectPath(projectKey)}/externalContacts/${externalUserId}`;

        // Check if contact already exists
        const existingSnapshot = await get(ref(this.db, contactPath));
        if (existingSnapshot.exists()) {
            throw new Error('Denna kontakt finns redan i projektet');
        }

        const contactData = {
            externalUserId,
            contactType,
            addedBy: user.uid,
            addedAt: formatDateForStorage(new Date()),
            ...(projectNote?.trim() && { projectNote: projectNote.trim() })
        };

        await set(ref(this.db, contactPath), contactData);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Remove an external contact from a project
     */
    async removeExternalContact(
        buildingKey: string,
        projectKey: string,
        externalUserId: string
    ): Promise<void> {
        const contactRef = ref(this.db, `${this.getProjectPath(projectKey)}/externalContacts/${externalUserId}`);
        await remove(contactRef);
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }

    /**
     * Update the project note for an external contact
     */
    async updateExternalContactNote(
        buildingKey: string,
        projectKey: string,
        externalUserId: string,
        note: string
    ): Promise<void> {
        const contactRef = ref(this.db, `${this.getProjectPath(projectKey)}/externalContacts/${externalUserId}`);

        // Check contact exists
        const snapshot = await get(contactRef);
        if (!snapshot.exists()) {
            throw new Error('Kontakten hittades inte');
        }

        if (note.trim()) {
            await update(contactRef, { projectNote: note.trim() });
        } else {
            // Remove projectNote if empty
            const updates: Record<string, string | null> = {};
            updates[`${this.getProjectPath(projectKey)}/externalContacts/${externalUserId}/projectNote`] = null;
            await update(ref(this.db), updates);
        }
        recordActivity('project.updated', { buildingKey, targetKey: projectKey });
    }
}

// Export singleton instance
export const projectsApi = new ProjectsApi();
